import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Plus, Save } from "lucide-react";

interface HomePageContent {
  id: string;
  section: string;
  content: string;
  image_url: string | null;
  created_at: string;
}

export const HomePageEditor = () => {
  const [content, setContent] = useState<HomePageContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editContent, setEditContent] = useState({
    id: "",
    content: "",
    image_url: ""
  });
  const { toast } = useToast();

  const fetchContent = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('home_page_content')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      setContent(data);
    } catch (err) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to fetch home page content",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (section: HomePageContent) => {
    setEditingSection(section.id);
    setEditContent({
      id: section.id,
      content: section.content,
      image_url: section.image_url || ""
    });
  };

  const handleSave = async () => {
    try {
      const { error } = await supabase
        .from('home_page_content')
        .update({
          content: editContent.content,
          image_url: editContent.image_url
        })
        .eq('id', editContent.id);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Content updated successfully",
      });
      
      setEditingSection(null);
      setEditContent({
        id: "",
        content: "",
        image_url: ""
      });
      fetchContent();
    } catch (err) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to update content",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Home Page Editor</CardTitle>
              <CardDescription>
                Edit and manage home page content sections
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {content.map((section) => (
              <div key={section.id} className="space-y-2">
                <h3 className="font-semibold">{section.section}</h3>
                {editingSection === section.id ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editContent.content}
                      onChange={(e) => setEditContent({ ...editContent, content: e.target.value })}
                      placeholder="Enter content..."
                      className="min-h-[100px]"
                    />
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Image URL</label>
                      <input
                        type="url"
                        value={editContent.image_url}
                        onChange={(e) => setEditContent({ ...editContent, image_url: e.target.value })}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditingSection(null);
                          setEditContent({
                            id: "",
                            content: "",
                            image_url: ""
                          });
                        }}
                      >
                        Cancel
                      </Button>
                      <Button onClick={handleSave}>
                        <Save className="mr-2 h-4 w-4" />
                        Save Changes
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div dangerouslySetInnerHTML={{ __html: section.content }} />
                    {section.image_url && (
                      <img
                        src={section.image_url}
                        alt={section.section}
                        className="mt-2 rounded-md"
                      />
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(section)}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Edit Section
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
